import { QueryKey, useQuery } from '@tanstack/react-query'
import type { BackendQueryOptions } from 'hooks/backend/api'
import { fetchBackendJson } from 'hooks/backend/api'
import { useConnection } from 'hooks/useConnection'
import { parseUnits } from 'viem'

type BaultResponse = {
  id: string
  name: string
  symbol: string
  apy: number
  stakingToken: string
  stakingTokenSymbol: string
  stakingTokenDecimals: number
  sharePrice: number
  totalAssets: number
  totalSupply: number
  bounty: string
  compoundFeeBps: number
  exitFeeBps: number
  rewardVault: string | null
  paused: boolean
  updatedAt: string | null
  createdAt: number | null
  createdAtBlock: number | null
  compoundCount: number
  lastCompoundAt: number | null
  averageCompoundInterval: number | null
}

type BaultCompoundResponse = {
  compoundNumber: number
  timestamp: number
  blockNumber: number
  transactionHash: string
  origin: string
  bountySender: string
  bgtRecipient: string
  isWrapperClaim: boolean
  bgtWrapper: string | null
  compoundAmount: string
  compoundFee: string
  tokenToRecipient: string
  bounty: string
  sharePrice: number
  apy: number
}

type ListResponse<T> = { data: T[]; count: number }

/**
 * Bault in the subgraph's shape: token amounts in wei, timestamps in unix seconds, all as strings.
 * Creation fields are null until the backend indexer reaches the bault's deployment.
 */
export type BaultEntity = {
  id: string
  name: string
  symbol: string
  apy: string
  apyTimestamp: string
  bounty: string
  paused: boolean
  sharePrice: string
  compoundCount: number
  compoundFeeBps: number
  exitFeeBps: number
  averageCompoundInterval: string | null
  createdAt: string | null
  createdAtBlock: string | null
  lastUpdatedAt: string
  totalAssets: string
  totalSupply: string
  rewardVault: string | null
  lastCompoundAt: string | null
  stakingToken: { id: string; symbol: string; decimals: number }
}

export type IndexedBaultEntity = BaultEntity & { createdAt: string; createdAtBlock: string }

export type BaultPricePoint = {
  timestamp: string
  sharePrice: string
  apy: string
  compoundAmount: string
  compoundNumber: number
  origin: string
  isWrapperClaim: boolean
  bgtAmount: string | null
  wrapperTokenAmount: string | null
  bountyAmount: string
}

type ClaimBase = {
  id: string
  bault: { id: string }
  bountySender: { id: string }
  bgtRecipient: string
  compoundAmount: string
  compoundFee: string
  origin: string
  timestamp: string
  blockNumber: string
  transactionHash: string
}

export type BgtClaim = ClaimBase & { bgtAmount: string }
export type BgtWrapperClaim = ClaimBase & { wrapperTokenAmount: string; bgtWrapperAddress: { id: string } }

type Compounds = { bgtClaims: BgtClaim[]; bgtWrapperClaims: BgtWrapperClaim[] }
type Analytics = { bault: IndexedBaultEntity | null; pricePoints: BaultPricePoint[] }

/** Most compounds the backend returns per page. */
const MAX_COMPOUNDS_PAGE = 1000

const BAULT_DECIMALS = 18

/** The backend keeps amounts as 18-decimal floats, so these wei values are approximate: display only. */
const toWei = (amount: number) => parseUnits(amount.toFixed(BAULT_DECIMALS), BAULT_DECIMALS).toString()

const nowInSeconds = () => Math.floor(Date.now() / 1000)

const isIndexed = (bault: BaultEntity): bault is IndexedBaultEntity => bault.createdAt !== null

const toBaultEntity = (bault: BaultResponse): BaultEntity => {
  const updatedAt = String(bault.updatedAt ? Math.floor(Date.parse(bault.updatedAt) / 1000) : nowInSeconds())
  return {
    id: bault.id,
    name: bault.name,
    symbol: bault.symbol,
    apy: String(bault.apy),
    apyTimestamp: updatedAt,
    bounty: bault.bounty,
    paused: bault.paused,
    sharePrice: String(bault.sharePrice),
    compoundCount: bault.compoundCount,
    compoundFeeBps: bault.compoundFeeBps,
    exitFeeBps: bault.exitFeeBps,
    averageCompoundInterval: bault.averageCompoundInterval === null ? null : String(bault.averageCompoundInterval),
    createdAt: bault.createdAt === null ? null : String(bault.createdAt),
    createdAtBlock: bault.createdAtBlock === null ? null : String(bault.createdAtBlock),
    lastUpdatedAt: updatedAt,
    totalAssets: toWei(bault.totalAssets),
    totalSupply: toWei(bault.totalSupply),
    rewardVault: bault.rewardVault,
    lastCompoundAt: bault.lastCompoundAt === null ? null : String(bault.lastCompoundAt),
    stakingToken: {
      id: bault.stakingToken,
      symbol: bault.stakingTokenSymbol,
      decimals: bault.stakingTokenDecimals,
    },
  }
}

const toPricePoint = (compound: BaultCompoundResponse): BaultPricePoint => ({
  timestamp: String(compound.timestamp),
  sharePrice: String(compound.sharePrice),
  apy: String(compound.apy),
  compoundAmount: compound.compoundAmount,
  compoundNumber: compound.compoundNumber,
  origin: compound.origin,
  isWrapperClaim: compound.isWrapperClaim,
  bgtAmount: compound.isWrapperClaim ? null : compound.tokenToRecipient,
  wrapperTokenAmount: compound.isWrapperClaim ? compound.tokenToRecipient : null,
  bountyAmount: compound.bounty,
})

const toCompounds = (baultId: string, compounds: BaultCompoundResponse[]): Compounds => {
  const result: Compounds = { bgtClaims: [], bgtWrapperClaims: [] }

  for (const compound of compounds) {
    const base: ClaimBase = {
      id: `${compound.transactionHash}-${compound.compoundNumber}`,
      bault: { id: baultId },
      bountySender: { id: compound.bountySender },
      bgtRecipient: compound.bgtRecipient,
      compoundAmount: compound.compoundAmount,
      compoundFee: compound.compoundFee,
      origin: compound.origin,
      timestamp: String(compound.timestamp),
      blockNumber: String(compound.blockNumber),
      transactionHash: compound.transactionHash,
    }

    if (!compound.isWrapperClaim) {
      result.bgtClaims.push({ ...base, bgtAmount: compound.tokenToRecipient })
    } else if (compound.bgtWrapper) {
      result.bgtWrapperClaims.push({
        ...base,
        wrapperTokenAmount: compound.tokenToRecipient,
        bgtWrapperAddress: { id: compound.bgtWrapper },
      })
    }
  }

  return result
}

const isNotFound = (error: unknown) => (error as { status?: number } | null)?.status === 404

const fetchBault = async (chainId: number, id: string, signal?: AbortSignal): Promise<BaultResponse | null> => {
  try {
    return await fetchBackendJson<BaultResponse>(`/baults/${id}?chainId=${chainId}`, { signal })
  } catch (error) {
    if (isNotFound(error)) return null
    throw error
  }
}

/** Newest first; empty while the backend has not indexed the bault yet. */
const fetchCompounds = async (
  chainId: number,
  id: string,
  limit: number,
  signal?: AbortSignal
): Promise<BaultCompoundResponse[]> => {
  try {
    const { data } = await fetchBackendJson<ListResponse<BaultCompoundResponse>>(
      `/baults/${id}/compounds?chainId=${chainId}&limit=${Math.min(limit, MAX_COMPOUNDS_PAGE)}&orderDirection=desc`,
      { signal }
    )
    return data
  } catch (error) {
    if (isNotFound(error)) return []
    throw error
  }
}

const baultKeyBase = (chainId: number) => ['bault', chainId] as const

export const baultQueryKeys = {
  all: (chainId: number) => baultKeyBase(chainId),
  byId: (chainId: number, id: string) => [...baultKeyBase(chainId), 'byId', id] as const,
  allBaults: (chainId: number) => [...baultKeyBase(chainId), 'list'] as const,
  compounds: (chainId: number, id: string, first: number) =>
    [...baultKeyBase(chainId), 'compounds', id, first] as const,
  analytics: (chainId: number, id: string) => [...baultKeyBase(chainId), 'analytics', id] as const,
}

const useBaultQuery = <TData>(
  defaultQueryKey: QueryKey,
  queryFn: (signal: AbortSignal) => Promise<TData>,
  isReady: boolean,
  options?: BackendQueryOptions<TData>
) => {
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    ...queryOptions
  } = options ?? {}

  return useQuery<TData, Error>({
    queryKey: queryKey ?? defaultQueryKey,
    queryFn: ({ signal }) => queryFn(signal),
    enabled: (optionEnabled ?? true) && !skip && isReady,
    retry,
    refetchOnWindowFocus,
    refetchOnReconnect,
    ...queryOptions,
  })
}

export const useBaultById = (id: string | null | undefined, options?: BackendQueryOptions<BaultEntity | null>) => {
  const { chainId } = useConnection()
  const normalizedId = (id ?? '').toLowerCase()

  return useBaultQuery(
    baultQueryKeys.byId(chainId, normalizedId),
    async (signal) => {
      const bault = await fetchBault(chainId, normalizedId, signal)
      return bault ? toBaultEntity(bault) : null
    },
    Boolean(normalizedId),
    options
  )
}

export const useAllBaults = (options?: BackendQueryOptions<IndexedBaultEntity[]>) => {
  const { chainId } = useConnection()

  return useBaultQuery(
    baultQueryKeys.allBaults(chainId),
    async (signal) => {
      const { data } = await fetchBackendJson<ListResponse<BaultResponse>>(`/baults?chainId=${chainId}`, { signal })
      return data
        .filter((bault) => !bault.paused)
        .sort((a, b) => b.totalAssets - a.totalAssets)
        .map(toBaultEntity)
        .filter(isIndexed)
    },
    true,
    options
  )
}

export const useBaultCompounds = (params: { id: string; first?: number }, options?: BackendQueryOptions<Compounds>) => {
  const { chainId } = useConnection()
  const normalizedId = (params.id ?? '').toLowerCase()
  const first = params.first ?? 10

  return useBaultQuery(
    baultQueryKeys.compounds(chainId, normalizedId, first),
    async (signal) => {
      const compounds = await fetchCompounds(chainId, normalizedId, first, signal)
      return toCompounds(normalizedId, compounds)
    },
    Boolean(normalizedId),
    options
  )
}

export const useBaultAnalytics = (params: { id: string }, options?: BackendQueryOptions<Analytics>) => {
  const { chainId } = useConnection()
  const normalizedId = (params.id ?? '').toLowerCase()

  return useBaultQuery(
    baultQueryKeys.analytics(chainId, normalizedId),
    async (signal) => {
      const [bault, compounds] = await Promise.all([
        fetchBault(chainId, normalizedId, signal),
        fetchCompounds(chainId, normalizedId, MAX_COMPOUNDS_PAGE, signal),
      ])
      const entity = bault ? toBaultEntity(bault) : null
      return {
        bault: entity && isIndexed(entity) ? entity : null,
        pricePoints: compounds.map(toPricePoint),
      }
    },
    Boolean(normalizedId),
    options
  )
}
